<script setup>
import { computed, ref, watch } from 'vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';

const props = defineProps({
  funnels: { type: Array, default: () => [] },
  modelValue: { type: [Array, Object, String, Number], default: null },
  dropdownMaxHeight: { type: String, default: 'max-h-80' },
});

const emit = defineEmits(['update:modelValue']);

const normalize = value => {
  if (Array.isArray(value)) return [value[0] ?? null, value[1] ?? null];
  return [null, null];
};

const selected = ref(normalize(props.modelValue));

watch(
  () => props.modelValue,
  newValue => {
    const next = normalize(newValue);
    if (next[0] !== selected.value[0] || next[1] !== selected.value[1]) {
      selected.value = next;
    }
  }
);

const funnelOptions = computed(() =>
  (props.funnels || []).map(f => ({ id: f.id, name: f.name }))
);

const currentFunnel = computed(() =>
  (props.funnels || []).find(f => f.id === selected.value[0])
);

const stageOptions = computed(() => {
  if (!currentFunnel.value) return [];
  const stages = currentFunnel.value.funnel_stages || [];
  return [...stages]
    .sort((a, b) => a.position - b.position)
    .map(s => ({ id: s.id, name: s.name }));
});

const funnelModel = computed({
  get() {
    if (!currentFunnel.value) return null;
    return { id: currentFunnel.value.id, name: currentFunnel.value.name };
  },
  set(value) {
    const funnelId = value?.id ?? value ?? null;
    selected.value = [funnelId, null];
    emit('update:modelValue', [funnelId, null]);
  },
});

const stageModel = computed({
  get() {
    const stageId = selected.value[1];
    if (!stageId) return null;
    const stage = (currentFunnel.value?.funnel_stages || []).find(
      s => s.id === stageId
    );
    return stage ? { id: stage.id, name: stage.name } : null;
  },
  set(value) {
    const stageId = value?.id ?? value ?? null;
    selected.value = [selected.value[0], stageId];
    emit('update:modelValue', [selected.value[0], stageId]);
  },
});
</script>

<template>
  <div class="flex items-center gap-2 flex-wrap">
    <SingleSelect
      v-model="funnelModel"
      :options="funnelOptions"
      :dropdown-max-height="dropdownMaxHeight"
      :placeholder="$t('AUTOMATION.ACTION.FUNNEL_PLACEHOLDER')"
    />
    <SingleSelect
      v-model="stageModel"
      :options="stageOptions"
      :disabled="!currentFunnel"
      :dropdown-max-height="dropdownMaxHeight"
      :placeholder="$t('AUTOMATION.ACTION.FUNNEL_STAGE_PLACEHOLDER')"
    />
  </div>
</template>
